import { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Stack,
  Typography,
  Box,
  TextField,
  IconButton,
} from "@mui/material";
import TableSkeleton from "../../components/TableSkeleton";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation, Translation } from "react-i18next";
import SearchIcon from "@mui/icons-material/Search";

// permission
import { useSelector } from "react-redux";
import { user } from "../../reducers/userSlice";
import { checkPermission } from "../../helper/PermissionHelper";
import { pageName } from "../../reducers/commonSlice";
import { AgreementApi } from "../../api/AgreementApi";

export default function AgreementList() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filter, setFilter] = useState();
  const [searchStr, setSearchStr] = useState();
  const [total, setTotal] = useState(0);
  const [list, setList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const [haveCreatePermission, setHaveCreatePermission] = useState(false);
  const currentUser = useSelector(user);
  const currentPage = useSelector(pageName);

  useEffect(() => {
    if (searchParams.get("page")) {
      setPage(parseInt(searchParams.get("page")));
    }
  }, []);

  useEffect(() => {
    const createPermission = checkPermission(
      currentUser,
      currentPage,
      "create"
    );
    setHaveCreatePermission(createPermission);
  }, [currentUser, currentPage]);

  useEffect(() => {
    fetchData();
  }, [page]);

  useEffect(() => {
    if (!filter || filter.length <= 0) return;
    setPage(1);
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await AgreementApi.list(page, pageSize, filter);
      setList(response.data);
      setTotal(response.total);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log(error);
    }
  };

  const createProduct = (id) => {
    navigate(`/settings/agreement/create`);
  };

  const viewDetail = (id) => {
    navigate(`/settings/agreement/${id}`);
  };

  const onChangePage = (value) => {
    setPage(value);
    setSearchParams({ page: value });
  };

  const onChangeSearchField = (value) => {
    setSearchStr(value);
  };

  const search = () => {
    setPage(1);
    setFilter([{ field: "name", value: searchStr }]);
  };

  return (
    <Container maxWidth={false}>
      <Card variant="outlined">
        <CardHeader
          title={
            <Translation>
              {(t, { i18n }) => (
                <Typography variant="h6">{t("settings.agreement")}</Typography>
              )}
            </Translation>
          }
          action={
            haveCreatePermission && (
              <Button
                color="inherit"
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={createProduct}
              >
                <Translation>
                  {(t, { i18n }) => t("agreement.new_agreement")}
                </Translation>
              </Button>
            )
          }
        />

        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              alignItems: "center",
              pb: 1,
            }}
          >
            <TextField
              size="small"
              placeholder={t("layout.search_by_title")}
              onChange={(event) => {
                onChangeSearchField(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key == "Enter") {
                  search();
                }
              }}
              InputProps={{
                endAdornment: (
                  <IconButton
                    edge="end"
                    onClick={() => {
                      search();
                    }}
                  >
                    <SearchIcon />
                  </IconButton>
                ),
              }}
            />
          </Box>
          <TableContainer elevation={0} component={Paper}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell width={"10%"}>
                    <Translation>
                      {(t, { i18n }) => (
                        <Box variant="caption">{t("layout.id")}</Box>
                      )}
                    </Translation>
                  </TableCell>
                  <TableCell width={"30%"}>
                    <Translation>
                      {(t, { i18n }) => (
                        <Box variant="caption">{t("layout.name")}</Box>
                      )}
                    </Translation>
                  </TableCell>
                  <TableCell width={"60%"}>
                    <Translation>
                      {(t, { i18n }) => (
                        <Box variant="caption">{t("layout.description")}</Box>
                      )}
                    </Translation>
                  </TableCell>
                </TableRow>
              </TableHead>
              {isLoading ? (
                <TableSkeleton row={pageSize} colSpan={5} />
              ) : (
                <TableBody>
                  {!list.length && (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        {t("layout.no_data")}
                      </TableCell>
                    </TableRow>
                  )}
                  {list.map((row) => (
                    <TableRow
                      hover
                      key={row.id}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                      onClick={() => viewDetail(row.id)}
                    >
                      <TableCell component="th" scope="row">
                        {row.id}
                      </TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell sx={{ maxWidth: 0 }}>
                        <Box
                          sx={{
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.description}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </CardContent>

        <CardActions sx={{ justifyContent: "space-between", px: 2 }}>
          <Typography variant="body2">
            {t("layout.total")}: {total}
          </Typography>
          <Stack spacing={2}>
            <Pagination
              page={page}
              count={Math.ceil(total / pageSize)}
              shape="rounded"
              onChange={(event, value) => {
                onChangePage(value);
              }}
            />
          </Stack>
        </CardActions>
      </Card>
    </Container>
  );
}
